import { HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AnnouncementSeverity } from "@app/modules/dashboard/components/comms/entities";
import { ONBOARDING_DATA_LS } from "@app/modules/dashboard/constants";
import { translateCheckinAttributes } from "@app/modules/hovering-checkin/utils";
import { REPORT_BUTTON_COOKIE } from "@app/shared/widgets/reports-window/reports-window.component";
import { brandStorageKey, currentRetailerStorageKey, localStorageKey, retailersStorageKey, userSessionStorageKey } from "@core/constants";
import { BasicRetailer, CheckIn, GlobalState, JwtSchema, Retailer, RetailerOrigin } from "@core/entities";
import { GlobalService } from "@core/services/global.service";
import { PickingPointCacheService } from "@core/services/picking-point-cache.service";
import { PopUpNotificationService } from "@core/services/popup-notification.service";
import * as GlobalAction from "@core/store/actions/global.action";
import { serializePickupPoints } from "@core/utils";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { StorageMap } from "@ngx-pwa/local-storage";
import { add, isAfter, isBefore } from "date-fns";
import { CookieService } from "ngx-cookie-service";
import { combineLatest, forkJoin, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { environment as env, environment } from "src/environments/environment";

@Injectable()
export class GlobalEffect {
  cookieService = inject(CookieService);

  constructor(
    private actions$: Actions,
    private globalService: GlobalService,
    private pickingPointCache: PickingPointCacheService,
    private popupService: PopUpNotificationService,
    private storage: StorageMap,
    private router: Router
  ) {}

  clearPickingPointCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GlobalAction.clearAll),
        tap(() => this.pickingPointCache.invalidate())
      ),
    { dispatch: false }
  );

  fetchComms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.fetchComms),
      mergeMap(({ retailer }) =>
        forkJoin([this.globalService.fetchClosedPickingPoints(), this.globalService.fetchComms()]).pipe(
          mergeMap(([closedPP, response]: [any, any]) => {
            function isWithinDateRange(s?: string, e?: string) {
              if (!s || !e) return true;
              const start = new Date(s);
              const end = new Date(e);
              const today = new Date();
              return isAfter(today, start) && isBefore(today, end);
            }
            function belongsToRetailerOrigin(e?: RetailerOrigin[]) {
              return e.includes(retailer?.origin) || (e ?? []).length === 0;
            }
            const mapped = response.map(r => ({ ...r.value, id: r.id }));
            const header = mapped.find(a => {
              return (
                a.type === "HEADER" &&
                belongsToRetailerOrigin(a.metaData.retailerOrigin ?? []) &&
                isWithinDateRange(a.metaData.start_date, a.metaData.end_date)
              );
            });
            let notifications: any[] = mapped.filter(a => {
              return (
                a.type === "NOTIFICATION" &&
                belongsToRetailerOrigin(a.metaData.retailerOrigin ?? []) &&
                isWithinDateRange(a.metaData.start_date, a.metaData.end_date)
              );
            });
            let announcements = mapped.filter(a => {
              return (
                a.type === "ANNOUNCEMENT" &&
                belongsToRetailerOrigin(a.metaData.retailerOrigin ?? []) &&
                isWithinDateRange(a.metaData.start_date, a.metaData.end_date)
              );
            });
            if ((closedPP.picking_points ?? []).length > 0) {
              notifications.unshift({
                id: 123,
                dismissed: false,
                info: [],
                severity: AnnouncementSeverity.URGENT,
                title: {
                  es: `Hay ${closedPP.picking_points.length} tienda${closedPP.picking_points.length > 1 ? "s" : ""} cerrada${
                    closedPP.picking_points.length > 1 ? "s" : ""
                  }`,
                  pt: `Há ${closedPP.picking_points.length} loja${closedPP.picking_points.length > 1 ? "s" : ""} fechada${
                    closedPP.picking_points.length > 1 ? "s" : ""
                  }`,
                },
                subtitle: {
                  es: `Se volverán a abrir a las ${closedPP.picking_points[0].reopen_time}. Fueron cerradas por automatización`,
                  pt: `Serão reabertas às ${closedPP.picking_points[0].reopen_time}. Foram fechadas por automação.`,
                },
                type: "NOTIFICATION",
                metaData: {
                  active: true,
                  dismissable: true,
                  id: "0002",
                  path: "/dashboard/picking-points",
                  targetRoles: [],
                  end_date: new Date(),
                  start_date: new Date(),
                  retailerOrigin: [RetailerOrigin.CARGO],
                },
                showable: true,
                redirectionModule: "/dashboard/picking-points",
              });
            }
            if (header) {
              header.title = header.title[environment.lang];
              header.subtitle = header.subtitle[environment.lang];
            }
            if (notifications) {
              notifications = notifications.map(n => ({ ...n, title: n.title[environment.lang], subtitle: n.subtitle[environment.lang] }));
            }
            if (announcements) {
              announcements = announcements.map(n => ({
                ...n,
                title: n.title ? n.title[environment.lang] : "",
                showable: true,
                subtitle: n.subtitle ? n.subtitle[environment.lang] : "",
                info: n.info.map(i => ({
                  image: i.image ? i.image[environment.lang] : "",
                  description: i.description ? i.description[environment.lang] : "",
                })),
              }));
            }
            const comms: GlobalState["comms"] = {
              header,
              announcements,
              notifications,
            };
            return [GlobalAction.setComms({ payload: comms })];
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ error, status, httpError });
            this.popupService.error(error);
            return of(GlobalAction.Empty());
          })
        )
      )
    )
  );

  getPickupPoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.fetchAllCheckin),
      mergeMap(() =>
        this.globalService.getAllCheckinCodes().pipe(
          mergeMap((response: any) => {
            const headerIds = Object.keys(response[0]).map(k => translateCheckinAttributes(k));
            const headers = Object.keys(response[0]);
            const parseResponse = response.map(r => ({
              ...r,
              checkin_code_url: `https://cargo.rappi.com.${env.code.toLowerCase()}/${r.checkin_code_url}`,
            }));
            this.globalService.downloadCSV(parseResponse, headerIds, headers, `checkin_${new Date().toISOString().split("T")[0]}`);
            return [];
          }),
          catchError((httpError: HttpErrorResponse) => {
            const { status, error } = httpError;
            console.error({ error, status });
            this.popupService.error(error);
            return of(GlobalAction.Empty());
          })
        )
      )
    )
  );

  recursivePickingPointFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.fetchPickingPoints),
      mergeMap(({ ascendingName, activeOnly, retailerId }) => {
        return this.storage.get(retailersStorageKey).pipe(
          map((available: BasicRetailer[]) => {
            return retailerId ? [retailerId] : available.map(a => a.id);
          }),
          mergeMap(retailerIds =>
            this.pickingPointCache.getPickingPoints(
              { scope: "global", retailerIds, ascendingName: !!ascendingName, activeOnly: !!activeOnly },
              (limit, offset) => this.globalService.getPickupPoints(limit, offset, ascendingName, retailerIds as number[], activeOnly),
              () => this.globalService.getPickingPointListChecksum(retailerIds as number[], activeOnly)
            )
          ),
          mergeMap((res: any) => {
            if (res.length > 0) {
              return [
                GlobalAction.setSerializedPickingPoints({ payload: serializePickupPoints(res) }),
                GlobalAction.setPickingPoints({ payload: res }),
              ];
            }
            return [
              GlobalAction.uiGlobal({
                ui: [{ name: "loadPickingPoints", value: false }],
              }),
            ];
          })
        );
      })
    )
  );

  gCheckin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.fetchCheckin),
      mergeMap(p =>
        this.globalService.getCheckinCode(p.pickingPointId).pipe(
          mergeMap((checkin: CheckIn) => {
            return [
              GlobalAction.setCheckin({ checkin }),
              GlobalAction.uiGlobal({
                ui: [
                  { name: "loadingCheckin", value: false },
                  { name: "failedCheckin", value: false },
                ],
              }),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error("Checkin Error", { httpError });
            const { error } = httpError;
            this.popupService.error(error);
            return of(
              GlobalAction.uiGlobal({
                ui: [
                  { name: "failedCheckin", value: true },
                  { name: "loadingCheckin", value: false },
                ],
              })
            );
          })
        )
      )
    )
  );

  getCurrentRetailer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.getCurrentRetailer),
      mergeMap(({ retailerId }) =>
        this.globalService.getCurrentRetailer(retailerId).pipe(
          mergeMap(retailer => {
            return [
              GlobalAction.uiGlobal({ ui: [{ name: "loadingRetailer", value: false }] }),
              GlobalAction.setCurrentRetailer({ retailer }),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error({ httpError });
            const { error } = httpError;
            this.popupService.error(error);
            return of(GlobalAction.Empty());
          })
        )
      )
    )
  );

  refreshCurrentRetailer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.refreshCurrentRetailer),
      mergeMap(() => this.storage.get(currentRetailerStorageKey)),
      mergeMap((d: Retailer) => {
        return this.globalService.getCurrentRetailer(d.id).pipe(
          mergeMap(retailer => {
            return [
              GlobalAction.uiGlobal({ ui: [{ name: "loadingRetailer", value: false }] }),
              GlobalAction.setCurrentRetailer({ retailer }),
            ];
          }),
          catchError((httpError: HttpErrorResponse) => {
            console.error({ httpError });
            const { error } = httpError;
            this.popupService.error(error);
            return of(GlobalAction.Empty());
          })
        );
      })
    )
  );

  setCurrentRetailer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GlobalAction.setCurrentRetailer),
        mergeMap(retailer => {
          return this.storage.set(currentRetailerStorageKey, { ...retailer.retailer, id: Number(retailer.retailer.id) });
        })
      ),
    { dispatch: false }
  );

  setBrand$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GlobalAction.setBrand),
        mergeMap(({ brand }) => this.storage.set(brandStorageKey, brand))
      ),
    { dispatch: false }
  );

  setRetailers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.setRetailers),
      mergeMap(({ retailers }) => {
        return combineLatest([
          this.storage.get(currentRetailerStorageKey),
          this.storage.get(localStorageKey),
          this.storage.get(userSessionStorageKey),
          this.storage.set(retailersStorageKey, retailers),
        ]).pipe(
          mergeMap(([current, auth, userSessionId, _]: [Retailer | undefined, JwtSchema, number, any]) => {
            if (!current || auth.id !== userSessionId || retailers.length === 1) {
              return [GlobalAction.getCurrentRetailer({ retailerId: retailers[0].id! })];
            } else {
              return [GlobalAction.getCurrentRetailer({ retailerId: current.id! })];
            }
          })
        );
      })
    )
  );

  setPickupPoints$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.setSerializedPickingPoints),
      map(() => GlobalAction.uiGlobal({ ui: [{ name: "loadPickingPoints", value: false }] }))
    )
  );

  fetchInitialData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.fetchInitialData),
      switchMap(({ retailerId }) => {
        return combineLatest([
          this.globalService.validateExistanceOfOrders().pipe(catchError(error => of({ error }))),
          this.globalService.validateExistanceOfPickingPoints().pipe(catchError(error => of({ error }))),
          of(retailerId),
        ]);
      }),
      mergeMap(([hasOrders, pickingPoints, retailerId]) => {
        const data = { hasOrders, hasPickingPoints: (pickingPoints ?? []).length > 0 };
        const res = [GlobalAction.getKustomerJWT(), GlobalAction.setInitialData(data)];
        if (data.hasOrders && data.hasPickingPoints) {
          return combineLatest([this.storage.set(ONBOARDING_DATA_LS + retailerId, "true")]).pipe(
            mergeMap(() => {
              return res;
            })
          );
        } else {
          return res;
        }
      }),
      catchError(() => {
        return [GlobalAction.uiGlobal({ ui: [{ name: "loadingOnboarding", value: false }] })];
      })
    )
  );

  fetchIsCashless$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.fetchIsCashless),
      mergeMap(({ retailerId }) =>
        this.globalService.validateCashlessness(retailerId).pipe(
          mergeMap(r => {
            return [GlobalAction.setSettlementAttribute({ settlement: { isCashless: (r?.billing_periods ?? []).length > 0 } })];
          }),
          catchError(() => {
            return [GlobalAction.setSettlementAttribute({ settlement: { isCashless: false } })];
          })
        )
      )
    )
  );

  handlePossibleReroute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GlobalAction.setInitialData),
        tap(e => {
          if (!e.hasOrders && !e.hasPickingPoints) {
            this.router.navigate(["/", "dashboard", "welcome"]);
          }
        })
      ),
    { dispatch: false }
  );

  getKustomerJWT$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.getKustomerJWT),
      mergeMap(() => {
        return this.globalService.getKustomerJWT().pipe(
          mergeMap(r => {
            return [GlobalAction.setKustomerJWT({ jwt: r.access_token })];
          }),
          catchError(() => {
            return [GlobalAction.setSettlementAttribute({ settlement: { isCashless: false } })];
          })
        );
      })
    )
  );

  getReportsForWindow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.getReports),
      mergeMap(({ retailerId }) => {
        return combineLatest(this.globalService.getReports(retailerId)).pipe(
          mergeMap((reports: any) => {
            return [
              GlobalAction.setReports({
                reports: reports
                  .flat()
                  .sort((a, b) => (isBefore(new Date(a.created_at), new Date(b.created_at)) ? 1 : -1))
                  .slice(0, 6),
              }),
            ];
          })
        );
      })
    )
  );

  setReportsWindowButton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalAction.setReportsButtonCookie),
      map(({ retailerId }) => {
        return this.cookieService.set(REPORT_BUTTON_COOKIE + retailerId, "true", add(new Date(), { hours: 1 }));
      }),
      map(() => {
        return GlobalAction.uiGlobal({ ui: [{ name: "showReportsWindowButton", value: true }] });
      })
    )
  );
}
